import CloseIcon from 'Assets/images/close-popup.svg';
import CalendarIcon from 'Assets/images/commons/calendar.svg';
import InfoIcon from 'Assets/images/commons/info.svg';
import CkEditorField from 'Components/Form/CkEditorField/CkEditorField';
import { DEFAULT_DOCUMENT_FILE_TYPES_UPLOAD } from 'Constants/commons';
import { HS_FILL_FORM_EVENT, HS_SERVICE_TYPE_CHAT } from 'Constants/health-summary';
import useIsHealthSummaryUploading from 'Hooks/files/useIsHealthSummaryUploading';
import useHover from 'Hooks/useHover';
import { InputField, SingleDatePickerField, TextAreaField } from 'Modules/Formik';
import AutoSave from 'Modules/Formik/AutoSave';
import FileUploadField from 'Modules/Formik/FileUploadField/FileUploadField';
import Space from 'Modules/Space';
import { fileUploadURL, healthSummaryFileUpload } from 'Redux/modules/files/actions';
import { consultUpdate } from 'Redux/modules/webChat/consult/actions';
import {
  getSummaryConfig,
  healthSummaryCreate,
  healthSummaryReset,
  healthSummaryRetrieve,
  healthSummaryUpdate,
} from 'Redux/modules/webChat/healthSummary/actions';
import { getHealthSummaryById } from 'Selectors/webChat/healthSummary';
import { Field, Formik } from 'formik';
import { isArray } from 'lodash';
import moment from 'moment';
import { bool, func, objectOf, shape } from 'prop-types';
import { useCallback, useEffect, useRef } from 'react';
import { ANCHOR_RIGHT, OPEN_UP } from 'react-dates/constants';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Button, Form, Message, MessageContent } from 'semantic-ui-react';
import { updateEntities } from 'Redux/modules/entities/actions';
import { WEBCHAT } from 'Constants/normalize-module';

import { SummarySchema } from '../validation';
import { MARKDOWN_EDITOR_PLUGINS } from '../../AINotes/editorConfig';
import useCheckHealthSummary from '../hooks/useCheckHealthSummary';

import styles from './SummaryContent.less';

const TITLE_SUMMARY = 'Consult Summary';
const DATE_FORMAT = 'MMM DD, YYYY';

const wrapStyles = { marginBottom: 8, maxHeight: 320 };

const useSummaryConfig = () => {
  const dispatch = useDispatch();
  const configs = useSelector(state => state.webChat.healthSummary.configs);
  useEffect(() => {
    dispatch(getSummaryConfig());
  }, [dispatch]);
  return configs;
};

const getError = error => {
  const errorFields = error?.data?.errors;
  if (errorFields && isArray(errorFields) && errorFields.length > 0)
    return errorFields[0]?.message ?? 'Some thing went wrong!';
  return error?.data?.message ?? 'Some thing went wrong';
};

const handleMarkdownOutput = data => {
  // Replace _ with * for italic formatting
  // Replace \n with '  \n' for break line
  // Replace unordered list markers * with - but not conflict with italic formatting
  const listPattern = /(^|\n)\s*\*\s+/g;
  const newData = data
    .replace(listPattern, '$1- ')
    .replace(/_(.*?)_/g, '*$1*')
    .replace(/\n/g, '  \n');
  return newData;
};

const useSummary = healthSummaries => {
  const dispatch = useDispatch();
  const healthSummaryId = healthSummaries[0]?.healthSummaryId;
  const doctorId = healthSummaries[0]?.doctorId;
  // }
  const summary = useSelector(state => getHealthSummaryById(state, healthSummaryId));

  useEffect(() => {
    if (healthSummaryId) dispatch(healthSummaryRetrieve({ doctorId, summaryId: healthSummaryId }));
  }, [dispatch, doctorId, healthSummaryId]);

  return {
    summary,
  };
};

const genInitialValues = (defaultTitle, summaryObj, configs) => {
  const { visitDoctorIf: visitDoctorIfList = [] } = configs;
  if (!!summaryObj && visitDoctorIfList.length) {
    const { title, summary, clinicalNotes, plan, privateNote, followUpDate, attachments } =
      summaryObj;

    const initialValues = {
      title,
      plan,
      privateNote,
      clinicalNotes,
      attachments: attachments ?? [],
      followUpDate: moment(followUpDate).isValid() ? moment(followUpDate) : null,
      summary,
      status: 'draft',
    };

    return initialValues;
  }

  return {
    title: defaultTitle,
    clinicalNotes: '',
    summary: '',
    plan: '',
    privateNote: '',
    followUpDate: null,
    attachments: [],
  };
};

const HEALTH_SUMMARY_STATUS = {
  DRAFT: 'draft',
  FINAL: 'final',
};

const SummaryContent = props => {
  const {
    onClose,
    onNext,
    doctor: { id: doctorId },
    hasFinish,
    consult,
    datePickerAppendToBody = false,
  } = props;
  const dispatch = useDispatch();
  const {
    healthSummary = [],
    createdAt,
    id: consultId,
    patient: { id: patientId },
  } = consult;

  const healthSM = useSelector(state => state.webChat.healthSummary);
  const { status: statusHS, error } = healthSM;
  const statusConsult = useSelector(state => state.webChat.consults.status);
  const configs = useSummaryConfig();
  const { summary } = useSummary(healthSummary);
  const defaultTitle = `${TITLE_SUMMARY} - ${moment(createdAt).format(DATE_FORMAT)}`;
  const initialValues = genInitialValues(defaultTitle, summary, configs);
  const [hoverRef, isHovered] = useHover();

  const { isHealthSummaryUploading, resetHealthSummaryUpload, showAlert } =
    useIsHealthSummaryUploading();

  const onSubmit = async values => {
    if (values.status === HEALTH_SUMMARY_STATUS.FINAL && isHealthSummaryUploading) {
      showAlert();
      return;
    }
    if (shallowEqual(values, initialValues)) {
      onNext();
      return;
    }

    let data = null;

    const apiBody = {
      title: values.title,
      clinicalNotes: handleMarkdownOutput(values.clinicalNotes?.trim() ?? ''),
      summary: values.summary ?? '',
      plan: handleMarkdownOutput(values.plan?.trim() ?? ''),
      privateNote: values.privateNote?.trim() ?? '',
      status: values.status,
      followUpDate: moment(values.followUpDate).isValid()
        ? values.followUpDate.format('YYYY-MM-DD')
        : undefined,
      // not pass attachments with uploadStatus is uploading or failed
      attachments: values.attachments.filter(
        file => file.uploadStatus === 'success' || !file.uploadStatus,
      ),
      // final status use when finish consult, backend need it to validation mandatory fields
    };

    if (summary) {
      data = await dispatch(
        healthSummaryUpdate({ doctorId, healthSummaryId: summary.id, body: apiBody }),
      ); // NOSONAR
    } else {
      const body = {
        ...apiBody,
        serviceType: HS_SERVICE_TYPE_CHAT,
        serviceItemId: consultId,
        patientId,
        doctorId,
      };
      const summaryResponse = await dispatch(healthSummaryCreate({ doctorId, body })); // NOSONAR
      data = await dispatch(
        consultUpdate(doctorId, consultId, { healthSummaryId: summaryResponse.data.id }),
      );
    }
    if (values.status === HEALTH_SUMMARY_STATUS.FINAL) onNext();
    return data;
  };

  const updating =
    statusHS.creating === 0 || statusHS.updating === 0 || statusConsult.updating === 0;

  const isNeedRequireMandatoryField = hasFinish;

  const gennarateLabel = useCallback(
    label => `${label} ${isNeedRequireMandatoryField ? '(*)' : ''}`,
    [isNeedRequireMandatoryField],
  );

  useEffect(() => {
    resetHealthSummaryUpload();
    return () => dispatch(healthSummaryReset());
  }, [dispatch, resetHealthSummaryUpload]);

  const handleSelectedFile = (_newValue, listValue) => {
    dispatch(healthSummaryFileUpload(listValue));
  };

  const formRef = useRef();

  useEffect(() => {
    const handleFillFormEvent = event => {
      const { clinicalNotes, plan } = event.detail;
      const currentValues = formRef.current.values;
      const newValues = {
        ...currentValues,
        clinicalNotes: currentValues?.clinicalNotes?.trim().length
          ? `${currentValues.clinicalNotes}\n${clinicalNotes}`
          : clinicalNotes,
        plan: currentValues.plan?.trim().length ? `${currentValues.plan}\n${plan}` : plan,
      };
      formRef.current.setValues(newValues);
    };

    window.addEventListener(HS_FILL_FORM_EVENT, handleFillFormEvent);

    return () => {
      window.removeEventListener(HS_FILL_FORM_EVENT, handleFillFormEvent);
    };
  }, [formRef]);

  const healthSummaryRetrieveAfterUpdate = useCallback(
    summaryData => {
      if (summaryData) {
        dispatch(updateEntities(WEBCHAT, 'healthSummaries', summaryData?.id, summaryData)); // NOSONAR
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [dispatch],
  );

  const { handleCheckHealthSummary } = useCheckHealthSummary();
  const {
    data: { enableAiChecker },
  } = useSelector(state => state.webChat.doctor);

  const handleCheckHealthSummaryWhenSubmit = async values => {
    if (enableAiChecker) {
      handleCheckHealthSummary(values, consultId);
    }
  };

  return (
    <div>
      <div className={styles.formWrapper}>
        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          enableReinitialize
          validateOnChange={false}
          validateOnBlur={false}
          innerRef={formRef}
          validationSchema={SummarySchema}>
          {({ handleSubmit, values, validateForm, setErrors }) => (
            <Form onSubmit={handleSubmit}>
              <div className={hasFinish ? styles.scrollable : null}>
                <Message icon className={styles.alert}>
                  <Space direction="horizontal" size={12}>
                    <img src={InfoIcon} alt="" />
                    <MessageContent>
                      The summary will be sent to patient after you finish consult
                    </MessageContent>
                  </Space>
                </Message>
                <Field
                  label={gennarateLabel('Title')}
                  name="title"
                  component={InputField}
                  placeholder="Title"
                />
                <CkEditorField
                  name="clinicalNotes"
                  label={
                    <Space size={4}>
                      <span>{gennarateLabel('Clinical Notes')}</span>
                      <span className={styles.noteSubLabel}>
                        <i>Patient will not see this note.</i>
                      </span>
                    </Space>
                  }
                  wrapStyles={wrapStyles}
                  plugins={MARKDOWN_EDITOR_PLUGINS}
                />
                <Field
                  label={gennarateLabel('Diagnosis')}
                  name="summary"
                  component={InputField}
                  placeholder="Diagnosis"
                />
                <CkEditorField
                  name="plan"
                  label={gennarateLabel('Plan')}
                  wrapStyles={wrapStyles}
                  plugins={MARKDOWN_EDITOR_PLUGINS}
                />

                <Space size={4}>
                  <span className={styles.followUpVisitLabel}>
                    Follow up visit <span className={styles.noteSubLabel}>(optional)</span>
                  </span>
                </Space>

                <Field
                  name="followUpDate"
                  component={SingleDatePickerField}
                  monthPick
                  readOnly
                  showClearDate={!!values.followUpDate}
                  anchorDirection={ANCHOR_RIGHT}
                  openDirection={OPEN_UP}
                  customCloseIcon={
                    // values.followUpDate ? (
                    //   <img className={styles.calendarIcon} src={CloseIcon} alt="calendar" />
                    // ) : null
                    <img
                      className={styles.calendarIcon}
                      ref={hoverRef}
                      src={isHovered ? CloseIcon : CalendarIcon}
                      alt="close popup"
                    />
                  }
                  customInputIcon={
                    !values.followUpDate ? (
                      <img className={styles.calendarIcon} src={CalendarIcon} alt="calendar" />
                    ) : null
                  }
                  isOutsideRange={day => day.isBefore(moment().subtract(1, 'day'))}
                  appendToBody={datePickerAppendToBody}
                />

                <Field
                  label={
                    <Space size={4}>
                      <span>
                        Note <span className={styles.noteSubLabel}>(optional)</span>
                      </span>
                      <span className={styles.noteSubLabel}>
                        <i>This is your private note. Patient will not see it.</i>
                      </span>
                    </Space>
                  }
                  name="privateNote"
                  component={TextAreaField}
                  placeholder="Note"
                />
                <Field
                  name="attachments"
                  component={FileUploadField}
                  onFileSelected={handleSelectedFile}
                  maxFile={1000}
                  allowFileType={DEFAULT_DOCUMENT_FILE_TYPES_UPLOAD}
                  uploadAction={fileUploadURL}
                  multiple
                  isPublic={false}
                />
              </div>

              {error && <div className="error">{getError(error)}</div>}
              <div className={styles.actions}>
                {hasFinish && (
                  <>
                    <Button
                      primary
                      content="Next"
                      onClick={() => {
                        validateForm().then(async errors => {
                          if (Object.keys(errors).length === 0) {
                            handleCheckHealthSummaryWhenSubmit(values);
                            const data = await onSubmit({
                              ...values,
                              status: HEALTH_SUMMARY_STATUS.FINAL,
                            });
                            // update health summary after finish consult
                            // to get the latest data to use in step 3
                            // this is temporary solution
                            healthSummaryRetrieveAfterUpdate(data?.data);
                          }
                        });
                      }}
                      loading={updating}
                      disabled={updating}
                    />
                    <Button content="Cancel" type="button" onClick={onClose} />
                  </>
                )}
                <AutoSave
                  onSave={val => {
                    setErrors({});
                    onSubmit({ ...val, status: HEALTH_SUMMARY_STATUS.DRAFT });
                  }}
                  delay={500}
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

SummaryContent.propTypes = {
  doctor: shape({}).isRequired,
  onClose: func,
  onNext: func,
  hasFinish: bool, // for summary right menu
  consult: objectOf(shape({})).isRequired,
  datePickerAppendToBody: bool,
};

SummaryContent.defaultProps = {
  onClose: () => false,
  onNext: () => false,
  hasFinish: true,
  datePickerAppendToBody: false,
};

export default SummaryContent;
